import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import './ImportModal.css';

/**
 * Universeller Import-Dialog für alle Logistik-Tabellen.
 *
 * Props:
 *   title          — Überschrift im Dialog
 *   columns        — Array von { key, label, required?, parse? }
 *                    key:      interner Feldname
 *                    label:    Spaltenname in der Excel-Vorlage (und Tabellenheader im Preview)
 *                    required: wenn true, Zeilen ohne diesen Wert werden übersprungen
 *                    parse:    optionale Transformationsfunktion (raw string → Wert)
 *   templateName   — Dateiname der Beispiel-Excel (ohne .xlsx)
 *   templateRows   — Array von Beispiel-Zeilen (Arrays in Spalten-Reihenfolge)
 *   buildTemplateWorkbook — optionaler Callback für modul-spezifische Excel-Vorlagen
 *   parseWorkbook  — optionaler Callback für modul-spezifische Parser
 *   previewColumns — optionale Spalten für die Vorschau, wenn parseWorkbook genutzt wird
 *   replaceLabel   — Text für den Ersetzen-Schalter
 *   showReplaceToggle — false blendet den Ersetzen-Schalter aus
 *   defaultReplace — Startwert des Ersetzen-Schalters
 *   onImport(rows) — Callback mit den geparsten, validierten Zeilen
 *   onClose        — Dialog schließen
 */
function ImportModal({
  title,
  columns,
  templateName,
  templateRows,
  buildTemplateWorkbook,
  parseWorkbook,
  previewColumns,
  replaceLabel = 'Bestehende Einträge für die betroffenen Tage vorher löschen',
  showReplaceToggle = true,
  defaultReplace = true,
  onImport,
  onClose,
}) {
  const [vorschau, setVorschau] = useState(null);   // { rows, fehler }
  const [dateiName, setDateiName] = useState('');
  const [importiert, setImportiert] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ersetzen, setErsetzen] = useState(defaultReplace);
  const inputRef = useRef();

  // ── Template herunterladen ───────────────────────────────────────────────
  function downloadTemplate() {
    if (buildTemplateWorkbook) {
      const wb = buildTemplateWorkbook(XLSX);
      XLSX.writeFile(wb, `${templateName}.xlsx`);
      return;
    }

    const header = columns.map(c => c.label);
    const aoa = [header, ...templateRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Spaltenbreiten setzen
    ws['!cols'] = columns.map(() => ({ wch: 22 }));

    // Kopfzeile fett formatieren (SheetJS benötigt Pro für volle Styles,
    // aber wir können zumindest die Breite setzen — reicht für Usability)
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daten');

    // Hinweis-Blatt hinzufügen
    const hinweisRows = [
      ['HINWEISE ZUR VORLAGE'],
      [''],
      ['• Erste Zeile ist die Kopfzeile — bitte nicht ändern'],
      ['• Pflichtfelder: ' + columns.filter(c => c.required).map(c => c.label).join(', ')],
      ['• Datum-Format: JJJJ-MM-TT (z.B. 2026-07-06)'],
      ['• Uhrzeit-Format: HH:MM (z.B. 08:30)'],
      ['• Regelmäßig: JA oder NEIN'],
      ['• Umlaute (ä, ö, ü, ß) sind problemlos möglich'],
    ];
    const wsH = XLSX.utils.aoa_to_sheet(hinweisRows);
    wsH['!cols'] = [{ wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsH, 'Hinweise');

    XLSX.writeFile(wb, `${templateName}.xlsx`);
  }

  // ── Datei einlesen & parsen ──────────────────────────────────────────────
  function handleDatei(file) {
    if (!file) return;
    setDateiName(file.name);
    setImportiert(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', codepage: 65001 });
        if (parseWorkbook) {
          const parsed = parseWorkbook(wb, XLSX);
          setVorschau({
            rows: parsed.rows || [],
            fehler: parsed.fehler || [],
            meta: parsed.meta || {},
            columns: parsed.columns || previewColumns,
          });
        } else {
          const ws = wb.Sheets[wb.SheetNames[0]];
          // raw: false → Zahlen/Daten als formatierte Strings
          const rawRows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });

          const fehler = [];
          const rows = [];

          rawRows.forEach((rawRow, i) => {
            const zeile = i + 2; // +2 weil Zeile 1 = Header
            const obj = {};
            let ok = true;

            columns.forEach(col => {
              // Flexibles Matching: Leerzeichen trimmen, case-insensitiv
              const matchKey = Object.keys(rawRow).find(
                k => k.trim().toLowerCase() === col.label.trim().toLowerCase()
              );
              const raw = matchKey ? String(rawRow[matchKey]).trim() : '';

              if (col.required && !raw) {
                fehler.push(`Zeile ${zeile}: Pflichtfeld „${col.label}" fehlt`);
                ok = false;
                return;
              }

              obj[col.key] = col.parse ? col.parse(raw) : (raw || null);
            });

            if (ok) rows.push(obj);
          });

          setVorschau({ rows, fehler, columns });
        }
      } catch (err) {
        setVorschau({ rows: [], fehler: [`Datei konnte nicht gelesen werden: ${err.message}`] });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // ── Import ausführen ─────────────────────────────────────────────────────
  async function handleImport() {
    if (!vorschau || (vorschau.rows.length === 0 && !vorschau.meta?.dienste?.length)) return;
    setLaeuft(true);
    try {
      await onImport(vorschau.rows, ersetzen, vorschau.meta || {});
      setImportiert({
        rows: vorschau.rows.length,
        dienste: vorschau.meta?.dienste?.length || 0,
      });
      setVorschau(null);
      setDateiName('');
    } catch (err) {
      setVorschau(v => ({ ...v, fehler: [...(v?.fehler || []), `Import fehlgeschlagen: ${err.message}`] }));
    }
    setLaeuft(false);
  }

  const vorschauColumns = vorschau?.columns || previewColumns || columns;
  const kannImportieren = vorschau && (vorschau.rows.length > 0 || vorschau.meta?.dienste?.length > 0);

  return (
    <div className="import-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="import-modal">
        <button className="import-close" onClick={onClose}>✕</button>
        <h2 className="import-title">{title}</h2>

        {/* Schritt 1: Vorlage */}
        <div className="import-step">
          <div className="import-step-num">1</div>
          <div className="import-step-body">
            <div className="import-step-label">Vorlage herunterladen & ausfüllen</div>
            <button className="import-btn download-btn" onClick={downloadTemplate}>
              <span className="material-symbols-outlined">download</span>
              Beispiel-Excel herunterladen
            </button>
          </div>
        </div>

        {/* Schritt 2: Hochladen */}
        <div className="import-step">
          <div className="import-step-num">2</div>
          <div className="import-step-body">
            <div className="import-step-label">Ausgefüllte Datei hochladen (.xlsx oder .csv)</div>
            <div
              className="import-dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleDatei(e.dataTransfer.files[0]); }}
            >
              <span className="material-symbols-outlined drop-icon">upload_file</span>
              <span>{dateiName || 'Datei auswählen oder hierher ziehen'}</span>
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={e => handleDatei(e.target.files[0])}
            />
          </div>
        </div>

        {/* Fehler */}
        {vorschau?.fehler?.length > 0 && (
          <div className="import-fehler">
            {vorschau.fehler.map((f, i) => <div key={i}>⚠ {f}</div>)}
          </div>
        )}

        {/* Vorschau-Tabelle */}
        {kannImportieren && (
          <div className="import-step">
            <div className="import-step-num">3</div>
            <div className="import-step-body">
              <div className="import-step-label">
                Vorschau ({vorschau.rows.length} Zeile{vorschau.rows.length !== 1 ? 'n' : ''} erkannt)
              </div>
              {vorschau.meta?.dienste?.length > 0 && (
                <div className="import-meta-note">
                  Dienstspalten: {vorschau.meta.dienste.map(d => d.Name || d).join(', ')}
                </div>
              )}
              {vorschau.rows.length > 0 && (
                <div className="import-preview-wrapper">
                  <table className="import-preview-table">
                    <thead>
                      <tr>{vorschauColumns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {vorschau.rows.slice(0, 6).map((row, i) => (
                        <tr key={i}>
                          {vorschauColumns.map(c => (
                            <td key={c.key}>{row[c.key] == null ? '' : String(row[c.key])}</td>
                          ))}
                        </tr>
                      ))}
                      {vorschau.rows.length > 6 && (
                        <tr><td colSpan={vorschauColumns.length} className="import-mehr">
                          … und {vorschau.rows.length - 6} weitere
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {showReplaceToggle && (
                <label className="import-ersetzen-toggle">
                  <input
                    type="checkbox"
                    checked={ersetzen}
                    onChange={e => setErsetzen(e.target.checked)}
                  />
                  {replaceLabel}
                </label>
              )}

              <div className="import-actions">
                <button
                  className="import-btn import-confirm-btn"
                  onClick={handleImport}
                  disabled={laeuft}
                >
                  {laeuft
                    ? 'Wird importiert …'
                    : `${vorschau.rows.length} Zeile${vorschau.rows.length !== 1 ? 'n' : ''} importieren`}
                </button>
                <button className="import-btn import-cancel-btn" onClick={() => { setVorschau(null); setDateiName(''); }}>
                  Zurücksetzen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Erfolgsmeldung */}
        {importiert !== null && (
          <div className="import-erfolg">
            <span className="material-symbols-outlined">check_circle</span>
            {importiert.rows} Einträge erfolgreich importiert.
            {importiert.dienste > 0 && ` ${importiert.dienste} Dienstspalte${importiert.dienste !== 1 ? 'n' : ''} geprüft.`}
            <button className="import-btn import-close-btn" onClick={onClose}>Schließen</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportModal;
